import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStore } from "devextreme-aspnet-data-nojquery";
import DataSource from "devextreme/data/data_source";
import AccordionReact from "devextreme-react/accordion";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { TextBox, DateBox } from "devextreme-react";
import {
    Column,
    ColumnChooser,
    ColumnChooserSearch,
    DataGrid,
    Export,
    FilterPanel,
    FilterRow,
    HeaderFilter,
    Lookup,
    Pager,
    Paging,
    Scrolling,
    Sorting,
    StateStoring,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import data from "../../assets/apiRoutes.json";
import { MESSAGES } from "../../common/Messages";
import GridLayoutManager from "../../gridLayoutCustomization/GridLayoutManager";
import "./bolPages.css";
import BolPagesPopup from "./BolPagesPopup";
import "../../common/css/searchPage.css";

/*
 * Freight Bills - Driver BOLs (T20260930.0001).
 *
 * The office half of Driver Bill of Lading Capture: which loads have had their paperwork sent in, and
 * which are still waiting. The pictures are opened from the BOL Pages count, or in ABECAS on the freight
 * bill itself - this screen is how the office sees, in one place, what has arrived.
 */

const GRID_KEY = "fm-driver-bols";

const searchpanel = [{ ID: 1, Title: MESSAGES.BOL_SEARCH_TITLE }];

/** A DateBox value as the model binder wants it, or null when the box is empty. */
const toIsoDate = (value) => {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

// FMTRANSHEADER.PODSTATUS, as ABFLAGFIELD defines it for this table.
const POD_STATUSES = [
    { Value: "A", Text: MESSAGES.BOL_POD_NOT_AVAILABLE },
    { Value: "M", Text: MESSAGES.BOL_POD_MISSING },
    { Value: "N", Text: MESSAGES.BOL_POD_NOT_REQUIRED },
    { Value: "Q", Text: MESSAGES.BOL_POD_REQUIRED },
    { Value: "R", Text: MESSAGES.BOL_POD_RECEIVED },
];

const emptyFields = { AnyNumber: "", DeliveredFrom: null, DeliveredTo: null };

const DriverBols = (props) => {
    const accessToken = props.oidc.user.access_token;
    const gridRef = useRef(null);

    const [currentLayout, setCurrentLayout] = useState(null);
    const [openedBill, setOpenedBill] = useState(null);
    const [selectedItems, setSelectedItems] = useState([searchpanel[0]]);
    const [fields, setFields] = useState(emptyFields);

    // Read through refs so changing a field does not rebuild the store - the grid keeps its paging,
    // sorting and column state across a search, as every other listing in the portal does.
    const fieldsRef = useRef(fields);
    fieldsRef.current = fields;

    const dataSource = useMemo(() => {
        const store = createStore({
            key: "RowId",
            loadParams: {
                "filter.SearchText": () => (fieldsRef.current.AnyNumber || "").trim() || null,
                "filter.DeliveredFrom": () => toIsoDate(fieldsRef.current.DeliveredFrom),
                "filter.DeliveredTo": () => toIsoDate(fieldsRef.current.DeliveredTo),
            },
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.FM.DriverBol.GetDriverBols}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };

                // An empty filter is no filter. Left in, an unset date is serialised as the literal
                // string "null", and the model binder answers "The value 'null' is not valid for
                // DeliveredFrom" - which the grid shows as "One or more validation errors occurred".
                //
                // The values have to be RESOLVED first: the store copies loadParams in verbatim
                // (extend(result, loadParams)) and only invokes the functions later, when the query
                // string is built, so at this point each one is still a function rather than its result.
                const params = ajaxOptions.data || {};
                Object.keys(params).forEach((key) => {
                    const raw = params[key];
                    const value = typeof raw === "function" ? raw() : raw;

                    if (value === null || value === undefined || value === "") {
                        delete params[key];
                    } else {
                        params[key] = value;
                    }
                });
            },
        });

        return new DataSource({ store });
    }, [accessToken]);

    const refresh = useCallback(() => {
        if (gridRef.current) gridRef.current.instance().refresh();
    }, []);

    const handleSearch = useCallback(() => refresh(), [refresh]);

    const handleReset = useCallback(() => {
        setFields(emptyFields);
        fieldsRef.current = emptyFields;
        refresh();
    }, [refresh]);

    const setField = useCallback((name, value) => {
        setFields((previous) => ({ ...previous, [name]: value }));
    }, []);

    return (
        <div className="search-page-container">
            <div className="search-page-content">
                <div id="accordion" className="search-page-accordion">
                    <AccordionReact
                        dataSource={searchpanel}
                        collapsible={true}
                        multiple={false}
                        animationDuration={300}
                        selectedItems={selectedItems}
                        onSelectionChanged={(e) => setSelectedItems(e.component.option("selectedItems"))}
                        itemTitleRender={() => (
                            <Toolbar className="accordian-toolbar">
                                <Item location="before">
                                    <div className="title">
                                        <h6 style={{ color: window._env_.Color2 }}>{MESSAGES.BOL_SEARCH_TITLE}</h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button
                                        className="accordianButton"
                                        onClick={handleSearch}
                                        text={MESSAGES.SEARCH}
                                        type="default"
                                    />
                                </Item>
                                <Item location="after">
                                    <Button
                                        className="accordianButton"
                                        onClick={handleReset}
                                        text={MESSAGES.RESET}
                                    />
                                </Item>
                            </Toolbar>
                        )}
                        itemRender={() => (
                            <div className="row">
                                <div className="col-md-4">
                                    <TextBox
                                        value={fields.AnyNumber}
                                        label={MESSAGES.BOL_SEARCH_ANY_NUMBER}
                                        labelMode="static"
                                        stylingMode="outlined"
                                        showClearButton={true}
                                        onValueChanged={(e) => setField("AnyNumber", e.value)}
                                        onEnterKey={handleSearch}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <DateBox
                                        value={fields.DeliveredFrom}
                                        label={MESSAGES.BOL_DELIVERED_FROM}
                                        labelMode="static"
                                        stylingMode="outlined"
                                        type="date"
                                        showClearButton={true}
                                        onValueChanged={(e) => setField("DeliveredFrom", e.value)}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <DateBox
                                        value={fields.DeliveredTo}
                                        label={MESSAGES.BOL_DELIVERED_TO_DATE}
                                        labelMode="static"
                                        stylingMode="outlined"
                                        type="date"
                                        showClearButton={true}
                                        onValueChanged={(e) => setField("DeliveredTo", e.value)}
                                    />
                                </div>
                            </div>
                        )}
                    />
                </div>

                <DataGrid
                    ref={gridRef}
                    dataSource={dataSource}
                    keyExpr="RowId"
                    showBorders={true}
                    columnAutoWidth={true}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    remoteOperations={true}
                    height="62vh"
                >
                    <StateStoring enabled={true} type="custom" />
                    <Scrolling mode="virtual" />
                    <Sorting mode="multiple" />
                    <FilterRow visible={true} />
                    <FilterPanel visible={true} />
                    <HeaderFilter visible={true} />
                    <Export enabled={true} />
                    <Paging defaultPageSize={20} />
                    <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={[10, 20, 50, 100]} />
                    <ColumnChooser enabled={true}>
                        <ColumnChooserSearch enabled={true} />
                    </ColumnChooser>

                    <GridToolbar>
                        <GridToolbarItem name="columnChooserButton" />
                        <GridToolbarItem name="exportButton" />
                        <GridToolbarItem location="after">
                            <GridLayoutManager
                                gridKey={GRID_KEY}
                                gridRef={gridRef}
                                accessToken={accessToken}
                                currentLayout={currentLayout}
                                setCurrentLayout={setCurrentLayout}
                            />
                        </GridToolbarItem>
                    </GridToolbar>

                    {/* Technical key, never shown. */}
                    <Column dataField="RowId" visible={false} allowFiltering={false} />

                    {/* Hidden rather than dropped: the mockup does not show these, but the column chooser is
                        where someone goes looking for them, and Record # is standard on every listing. */}
                    <Column dataField="RecordNumber" caption={MESSAGES.BOL_RECORD_NUMBER} visible={false} />
                    <Column dataField="InvoiceNumber" caption={MESSAGES.BOL_INVOICE_NUMBER} visible={false} />
                    <Column dataField="Commodity" caption={MESSAGES.BOL_COMMODITY} visible={false} />
                    <Column dataField="FbComment" caption={MESSAGES.BOL_BILLING_NOTE} visible={false} />

                    {/* The mockup's columns, in its order. */}
                    <Column dataField="TransactionNumber" caption={MESSAGES.BOL_FREIGHT_BILL} />
                    <Column dataField="BillOfLadingNumber" caption={MESSAGES.BOL_NUMBER} />
                    <Column dataField="ReferencePONumber" caption={MESSAGES.BOL_REFERENCE_NUMBER} />
                    <Column dataField="CustomerName" caption={MESSAGES.BOL_CUSTOMER} />
                    <Column dataField="ConsigneeName" caption={MESSAGES.BOL_DELIVERED_TO} />
                    <Column dataField="DeliveryDate" caption={MESSAGES.BOL_DELIVERED} dataType="date" />
                    <Column dataField="DriverName" caption={MESSAGES.BOL_DRIVER} />

                    {/* The field itself, shown as its description - codes confirmed from ABFLAGFIELD
                        (FMTRANSHEADER.PODSTATUS). Whether a BOL actually arrived is said by BOL Pages;
                        two different notions of "status" on one row would only contradict each other. */}
                    <Column dataField="PodStatus" caption={MESSAGES.BOL_POD_STATUS} width={150}>
                        <Lookup dataSource={POD_STATUSES} displayExpr="Text" valueExpr="Value" />
                    </Column>

                    {/* The count is the way in to the pages, as in the mockup. Only clickable when there is
                        something to open - a zero that invites a click and then shows nothing is worse than
                        a plain number. */}
                    <Column
                        dataField="BolPageCount"
                        caption={MESSAGES.BOL_PAGES}
                        dataType="number"
                        width={110}
                        cellRender={(cell) =>
                            cell.data && cell.data.BolPageCount > 0 ? (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    className="bolpages-link"
                                    onClick={() => setOpenedBill(cell.data)}
                                    onKeyDown={(e) => { if (e.key === "Enter") setOpenedBill(cell.data); }}
                                >
                                    {cell.data.BolPageCount}
                                </span>
                            ) : (
                                <span>{cell.value ?? 0}</span>
                            )
                        }
                    />
                </DataGrid>

                <BolPagesPopup
                    visible={openedBill != null}
                    freightBill={openedBill}
                    accessToken={accessToken}
                    onClose={() => setOpenedBill(null)}
                />

            </div>
        </div>
    );
};

export default connect((state) => ({ oidc: state.oidc }))(DriverBols);
